import React, { useCallback, useEffect, useState } from 'react'
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  LinearProgress,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import { oportunidadeService } from '@/services/oportunidadeService'
import { ScoreAderencia } from '@/types/oportunidade'

/**
 * Score de aderência da reserva — sempre com a decomposição à vista.
 *
 * O número sozinho seria mais uma ficção; o que torna o score útil é cada componente
 * dizer **o que falta** ("2 de 4 documentos entregues", "falta posicionamento
 * estratégico"). O consultor lê e sabe o próximo passo — que é o propósito real de um
 * score de aderência.
 *
 * Aderência ≠ Score Comercial (decisão 8.1, 28/09): o Score Comercial é derivado da etapa
 * pela tabela versionada do backend e vive no `ScoreComercialCard`; a aderência responde
 * outra pergunta — o que falta para instruir a reserva (Q5: mantida, com este rótulo).
 * Os dois convivem, cada um com seu rótulo. A probabilidade digitada saiu na 6.3.
 */

const corDoScore = (pct: number): 'success' | 'warning' | 'error' =>
  pct >= 80 ? 'success' : pct >= 50 ? 'warning' : 'error'

interface Props {
  oportunidadeId: string
}

const ScoreAderenciaCard: React.FC<Props> = ({ oportunidadeId }) => {
  const [score, setScore] = useState<ScoreAderencia | null>(null)
  const [carregando, setCarregando] = useState(true)

  const carregar = useCallback(async () => {
    setCarregando(true)
    try {
      setScore(await oportunidadeService.scoreAderencia(oportunidadeId))
    } catch {
      // Score é informativo: falha de cálculo não merece um alerta gritando na tela —
      // o cartão simplesmente não aparece e a reserva continua operável.
      setScore(null)
    } finally {
      setCarregando(false)
    }
  }, [oportunidadeId])

  useEffect(() => {
    void carregar()
  }, [carregar])

  if (carregando) {
    return (
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ display: 'flex', justifyContent: 'center' }}>
          <CircularProgress size={24} aria-label="Calculando aderência" />
        </CardContent>
      </Card>
    )
  }
  if (!score) return null

  const pct = score.maximo === 0 ? 0 : Math.round((score.score / score.maximo) * 100)

  return (
    <Card sx={{ mb: 3 }}>
      <CardHeader
        title="Aderência da reserva"
        subheader="O quanto a reserva está instruída — some os componentes e chega no número"
      />
      <CardContent>
        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
          <Typography variant="h4" sx={{ fontWeight: 700 }} color={`${corDoScore(pct)}.main`}>
            {score.score}
          </Typography>
          <Box sx={{ flex: 1 }}>
            <LinearProgress
              variant="determinate"
              value={pct}
              color={corDoScore(pct)}
              sx={{ height: 8, borderRadius: 4 }}
            />
            <Typography variant="caption" color="textSecondary">
              de {score.maximo}
            </Typography>
          </Box>
        </Stack>

        <Stack spacing={1.5}>
          {score.componentes.map((c) => (
            <Box key={c.nome}>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {c.nome}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {c.pontos}/{c.maximo}
                </Typography>
              </Stack>
              <Tooltip title={c.evidencia}>
                <LinearProgress
                  variant="determinate"
                  value={c.maximo === 0 ? 0 : (c.pontos / c.maximo) * 100}
                  sx={{ height: 4, borderRadius: 2, my: 0.5 }}
                />
              </Tooltip>
              <Typography variant="caption" color="textSecondary">
                {c.evidencia}
              </Typography>
            </Box>
          ))}
        </Stack>
      </CardContent>
    </Card>
  )
}

export default ScoreAderenciaCard
